"use client";

import React, { useEffect, useMemo } from "react";
import { t, Trans } from "@lingui/macro";
import Box from "@mui/material/Box";
import { useLingui } from "@lingui/react";
import Increments from "@/product/_components/increments";
import OfferzPlus from "@/product/_components/offerz-plus-box";
import ProductTabs from "@/product/_components/tabs";
import ImageSection from "@/product/_components/image-section";
import ProductInfo from "@/product/_components/product-info";
import QuantitySelector from "@/components/core/quantity-selector";
import ProductActions from "@/product/_components/product-actions";
import ProductOptions from "@/product/_components/product-options";
import MaximumInfo from "./maximum-info";
import AlertComponent from "@/components/core/alert";
import { useAlertStore } from "@/components/context/alert-store";
import CustomBreadcrumbs from "../../../category/(components)/custom-breadcrumbs";
import { Button, Typography } from "@mui/material";
import Link from "next/link";
import Image from "next/image";
import Banner from "@/components/core/banner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons/faInfo";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import OwnAmountInput from "./own-amount-input";
import { useProductTracking } from "@/hooks/use-product-tracking";
import { useProductStore } from "@/components/context/product-store";
import dynamic from "next/dynamic";
import { useXNoticeStore } from "@/components/context/xnotice-store";
import {
  IHighestRuleAmountProps,
  IOfferzAdditionalProducts,
  IOfferzBuyOptions,
  IOfferzOptions,
  IOfferzProductTabs,
  IOfferzSimilarProducts,
  IProduct,
} from "@/types/product";
import { magicConstants } from "@/utils/magic-constants";

const AdditionalProducts = dynamic(() => import("./additional-products"), {
  ssr: false,
});

interface ProductProps {
  buyOptions: IOfferzBuyOptions[];
  category: ParentCategories[];
  errorNotFound: boolean;
  highestRuleAmount: IHighestRuleAmountProps;
  minimum: number;
  maximum: number;
  offerzAdditionalProducts: IOfferzAdditionalProducts[];
  offerzOptions: IOfferzOptions;
  offerzProductTabs: IOfferzProductTabs[];
  offerzSimilarProducts: IOfferzSimilarProducts[];
  product: IProduct | null;
}

interface IProductProps {
  id: string;
  lang: string;
  data: ProductProps;
}
interface ParentCategories {
  id: number;
  name: string;
  parent_id: number;
  seo_url: string;
}

const Product: React.FC<IProductProps> = ({ id, lang, data }) => {
  const { i18n } = useLingui();
  const { message, setMessage } = useAlertStore();
  const resetQty = useProductStore((state) => state.resetQty);
  const resetOwnAmount = useProductStore((state) => state.resetOwnAmount);
  const { setError } = useProductStore();

  const { setProductID, setCategoryIDs, setManufacturerID } = useXNoticeStore();
  useEffect(() => {
    resetQty();
    setError(false);
    resetOwnAmount();
    if (data.product) {
      setProductID(String(data.product.id));
      setManufacturerID(String(data.product.manufacturer));

      const categories: any[] = [];
      data.product.category.forEach((category: any) => {
        categories.push(String(category.id));
      });
      setCategoryIDs(categories);
    }
  }, []);

  const {
    product,
    offerzSimilarProducts,
    offerzProductTabs,
    offerzOptions,
    buyOptions,
    minimum,
    maximum,
    offerzAdditionalProducts,
    category,
    highestRuleAmount,
    errorNotFound,
  } = data;

  const { ownAmountFlag } = useMemo(() => {
    const isSerialFlag = buyOptions.some(
      (option: IOfferzBuyOptions) => option.option_id === magicConstants.serialNumberOptionID,
    );
    const isOwnAmountFlag = buyOptions.some(
      (option: IOfferzBuyOptions) => option.option_id === magicConstants.priceOptionID,
    );

    return {
      serialFlag: isSerialFlag,
      ownAmountFlag: isOwnAmountFlag,
    };
  }, []);

  useProductTracking(product, offerzSimilarProducts);

  const isZak = product?.id === 4610;

  const generateBreadcrumbs = (categories: ParentCategories[]) => {
    const breadcrumbs: { title: string; link?: string | null }[] = [];

    if (errorNotFound) {
      return [{ title: t(i18n)`The page your requested cannot be found!` }];
    }

    const buildBreadcrumbTrail = (categoryId: number) => {
      const category = categories.find((cat) => cat.id === categoryId);

      if (category) {
        if (category.parent_id !== 0) {
          buildBreadcrumbTrail(category.parent_id);
        }

        breadcrumbs.push({
          title: category.name,
          link: category.seo_url ? `/category/${category.seo_url}` : null,
        });
      }
    };

    const lastCategory = categories.find((cat: ParentCategories) => cat.parent_id !== 0);
    if (lastCategory) {
      buildBreadcrumbTrail(lastCategory.id);
    }

    breadcrumbs.push({
      title: product?.name || "",
    });

    return breadcrumbs;
  };

  return (
    <main className="container flex h-full flex-col xs:pt-[75px] md:pt-0">
      {message && <AlertComponent message={message} isMain={true} setMessage={setMessage} />}

      <CustomBreadcrumbs crumbs={generateBreadcrumbs(category)} />
      {errorNotFound && (
        <div className="mb-[60px] flex min-h-[400px] flex-col">
          <Typography sx={{ fontWeight: 600, fontSize: { xs: "21px", md: "26px" }, mb: 3 }}>
            <Trans>The page you requested cannot be found!</Trans>
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            <Trans>Despite all efforts, the requested page cannot be found.</Trans>
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            <Trans>If the problem persists, please contact us - thank you very much.</Trans>
          </Typography>
          <Link prefetch={false} href="/" className="mt-5 self-end">
            <Button variant="contained">
              <Trans>Continue</Trans>
            </Button>
          </Link>
          <Banner
            subTitle={t(i18n)`Offerz Voucher Designer`}
            title={t(i18n)`Give personalized vouchers`}
            paragraph={t(
              i18n,
            )`With our voucher designer, you can easily give personalized vouchers with text, video, or photo messages!`}
            buttonText={t(i18n)`Go to designer`}
            buttonLink="/create-gift"
            rightPadding="80px 30px 70px 30px"
          >
            <div className="relative flex basis-1/2 justify-center">
              <Image
                loading="lazy"
                className="hidden md:block"
                src="https://offerz.ch/image/catalog/woman-phone.png"
                alt="woman-phone-image"
                width={371}
                height={520}
              />
              <Image
                loading="lazy"
                className="-ml-[65px] md:hidden"
                src="https://offerz.ch/image/catalog/woman-phone_mobile.png"
                alt="woman-phone-mobile-image"
                width={203}
                height={317}
              />
              <Image
                loading="lazy"
                className="absolute left-[50%] top-[130px] hidden max-w-[250px] md:block"
                src="https://offerz.ch/image/catalog/mockup-designer.png"
                alt="phone-image"
                width={274}
                height={520}
              />
              <Image
                loading="lazy"
                className="absolute left-[50%] top-[80px] md:hidden"
                src="https://offerz.ch/image/catalog/mockup-designer.png"
                alt="phone-image"
                width={159}
                height={280}
              />
            </div>
          </Banner>
        </div>
      )}

      {product && (
        <Box
          sx={{
            display: "flex",
            gap: { xs: 0, md: 6 },
            width: "100%",
            pb: { xs: 0, md: "40px" },
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <ImageSection product={product} lang={lang} />
            {highestRuleAmount?.earn_amount !== 0 && (
              <Box
                sx={{
                  display: { xs: "none", md: "block" },
                  width: { xs: "100%", md: "80%" },
                }}
              >
                <OfferzPlus loyaltyPoints={highestRuleAmount.earn_amount} lang={lang} />
              </Box>
            )}
          </Box>

          <Box
            component="section"
            sx={{
              width: { xs: "100%", md: "50%" },
              pt: { sm: 0, md: 8 },
              display: "flex",
              flexDirection: "column",
              mt: { xs: product.images.length ? "16px" : "-40px", md: 0 },
            }}
          >
            <ProductInfo product={product} />

            {highestRuleAmount?.earn_amount !== 0 && (
              <Box sx={{ display: { xs: "block", md: "none" }, pt: 3 }}>
                <OfferzPlus loyaltyPoints={highestRuleAmount.earn_amount} lang={lang} />
              </Box>
            )}

            {offerzSimilarProducts.length > 0 && (
              <Increments
                offerzSimilarProducts={offerzSimilarProducts}
                seoUrl={decodeURIComponent(id)}
              />
            )}

            {ownAmountFlag && <OwnAmountInput buyOptions={buyOptions} />}

            <ProductOptions buyOptions={buyOptions} />

            <QuantitySelector minimum={minimum} maximum={maximum} label />

            <ProductActions product={product} offerzOptions={offerzOptions} lang={lang} />

            {maximum > 0 && <MaximumInfo maximum={maximum} />}
            {isZak && (
              <Box className="mt-3 flex items-start gap-4 bg-[#fff8e5] p-3">
                <Typography className="text-[13px]">
                  <Trans>
                    FREE (from CHF 10 purchase). CHF 50 discount will be automatically deducted at
                    checkout.
                  </Trans>
                </Typography>
                <div>
                  <FontAwesomeIcon
                    icon={faInfo as IconProp}
                    style={{
                      color: "#000",
                      backgroundColor: "#ffb300",
                      padding: "5px",
                      borderRadius: "50%",
                      height: "12px",
                      width: "12px",
                    }}
                  />
                </div>
              </Box>
            )}

            <div className="mb-8">
              <ProductTabs offerzProductTabs={offerzProductTabs} />
            </div>
          </Box>
        </Box>
      )}

      {offerzAdditionalProducts.length > 0 && (
        <AdditionalProducts offerzAdditionalProducts={offerzAdditionalProducts} lang={lang} />
      )}
    </main>
  );
};

export default React.memo(Product);
